/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * TableViewPage
 * -----------------------
 * A dynamic, paginated, searchable, filterable table view for any backend table.
 *
 * Responsibilities:
 * - Fetch table schema and records from backend (with pagination, search and filters)
 * - Render a toolbar (search, filters, export, create)
 * - Render a dynamic table with view/edit/delete/select actions
 * - Handle record deletion with confirmation and notifications
 * - Show loading and error states
 * - Integrate with breadcrumb context and theming
 */

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import envConfig from "../config/envConfig";
import APIHelper from "../api/APIHelper";
import ProgressBarDialog from "../components/common/ProgressBar";
import AlertDialog from "../components/common/AlertDialog";
import Paginator from "../components/common/Paginator";
import DynamicTable from "../components/applications/DynamicTableComponent";
import { useTheme } from "../theme/ThemeContext";
import Button from "../components/Button";
import { useBreadcrumbNames } from '../context/BreadCrumbNameContext';
import { Modal, Form } from "react-bootstrap";
import { retrieve } from "../utils/storage_helper";
import { useAlertDialog } from "../components/common/AlertDialogContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import TableActionsToolbar from "../components/TableActionsToolbar";
import AuditHistoryModal from "../components/common/AuditHistoryModal";
import { getFieldOptionsForRole } from "../utils/uiRulesHelper";




/**
 * Shared API helper instance configured with environment-specific
 * URLs and OAuth client credentials.
 */
const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const DEFAULT_PAGE_SIZE = 20;

const OPERATOR_OPTIONS = {
  string: [
    { label: "Contains", value: "contains", symbol: "∋" },
    { label: "Equals", value: "equals", symbol: "=" },
    { label: "Not Equals", value: "not_equals", symbol: "≠" },
    { label: "Starts With", value: "starts_with", symbol: "A.." },
    { label: "Ends With", value: "ends_with", symbol: "..Z" },
    { label: "Does Not Contain", value: "does_not_contain", symbol: "∌" },
    { label: "Is Empty", value: "is_empty", symbol: "∅" },
    { label: "Not Empty", value: "is_not_empty", symbol: "!∅" },
  ],
  numeric: [
    { label: "Equals", value: "equals", symbol: "=" },
    { label: "Not Equals", value: "not_equals", symbol: "≠" },
    { label: "Greater Than", value: "greater_than", symbol: ">" },
    { label: "Less Than", value: "lesser_than", symbol: "<" },
    { label: "Greater Than or Equal", value: "greater_than_equal", symbol: "≥" },
    { label: "Less Than or Equal", value: "lesser_than_equal", symbol: "≤" },
    { label: "Is Empty", value: "is_empty", symbol: "∅" },
    { label: "Not Empty", value: "is_not_empty", symbol: "!∅" },
  ],
  date: [
    { label: "Equals", value: "equals", symbol: "=" },
    { label: "Not Equals", value: "not_equals", symbol: "≠" },
    { label: "Greater Than", value: "greater_than", symbol: ">" },
    { label: "Less Than", value: "lesser_than", symbol: "<" },
    { label: "Greater Than or Equal", value: "greater_than_equal", symbol: "≥" },
    { label: "Less Than or Equal", value: "lesser_than_equal", symbol: "≤" },
    { label: "Is Empty", value: "is_empty", symbol: "∅" },
    { label: "Not Empty", value: "is_not_empty", symbol: "!∅" },
  ],
  boolean: [
    { label: "Equals", value: "equals", symbol: "=" },
    { label: "Not Equals", value: "not_equals", symbol: "≠" },
    { label: "Is Empty", value: "is_empty", symbol: "∅" },
    { label: "Not Empty", value: "is_not_empty", symbol: "!∅" },
  ]
};

const getOperatorsForType = (dtype) => {
  if (["integer", "float", "decimal", "numeric"].includes(dtype)) return OPERATOR_OPTIONS.numeric;
  if (["date", "datetime", "timestamp"].includes(dtype)) return OPERATOR_OPTIONS.date;
  if (dtype === "boolean") return OPERATOR_OPTIONS.boolean;
  return OPERATOR_OPTIONS.string;
};

// Helper: Transform filters for API (Timezone Conversion)
const processFiltersForApi = (filters, schema) => {
  if (!filters || !schema) return filters;

  const processed = [];
  filters.forEach(f => {
    const field = schema.fields?.find(field => field.name === f.column_key);
    const dtype = field?.data_type?.toLowerCase();

    // Timezone Handling: DateTime fields
    if (dtype === 'datetime' || dtype === 'timestamp') {
      const val = f.column_value;
      const op = f.filter_type;

      // Skip processing for unary operators (no value to convert)
      if (["is_empty", "is_not_empty"].includes(op)) {
        processed.push(f);
        return;
      }

      if (val) {
        const isDateOnly = !val.includes('T');
        const localDateStr = isDateOnly ? `${val}T00:00:00` : val;
        const d = new Date(localDateStr);

        if (!isNaN(d.getTime())) {
          const startUtc = d.toISOString();

          // Date-Only Equals -> Range
          if (isDateOnly && op === 'equals') {
            const endUtc = new Date(d.getTime() + 86400000).toISOString();
            processed.push({ ...f, column_value: startUtc, filter_type: 'greater_than_equal' });
            processed.push({ ...f, column_key: f.column_key, column_value: endUtc, filter_type: 'lesser_than' });
            return;
          }

          // All other cases -> Convert value to UTC
          processed.push({ ...f, column_value: startUtc });
          return;
        }
      }
    }

    // Default pass-through
    processed.push(f);
  });
  return processed;
};

/**
 * FiltersModal
 * ------------
 * Reusable modal for configuring column-level filters.
 *
 * Props:
 * - show: controls visibility of the modal
 * - onClose: callback when modal is dismissed
 * - onApply: callback invoked with normalized filters array
 * - tableSchema: schema describing available fields
 * - initialFilters: filters currently applied at the page level
 *
 * Behavior:
 * - Maintains local form state separate from parent filters
 * - Converts form state into the expected filters array on Apply
 * - Supports type-aware inputs for basic types (text/number/date)
 * - Special handling for boolean fields with radio-style toggles
 */
function FiltersModal({ show, onClose, onApply, tableSchema, initialFilters }) {
  const { theme } = useTheme();
  const [localFilters, setLocalFilters] = useState({});

  // Initialize local filter state from initialFilters whenever they change
  useEffect(() => {
    const init = {};
    if (initialFilters && Array.isArray(initialFilters)) {
      initialFilters.forEach(f => {
        init[f.column_key] = {
          value: f.column_value,
          operator: f.filter_type || "contains"
        };
      });
    }
    setLocalFilters(init);
  }, [initialFilters]);

  // Update local state when any input changes
  const handleChange = (key, field, val) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: val
      }
    }));
  };

  /**
   * Normalizes local filter state into the filters array format:
   * - Skips empty values
   */
  const handleApply = () => {
    const filtersArray = [];
    for (const [key, data] of Object.entries(localFilters)) {
      const val = data?.value;
      const op = data?.operator;

      // Allow value to be empty if operator is unary (is_empty, is_not_empty)
      const isUnary = ["is_empty", "is_not_empty"].includes(op);

      if (isUnary || (val !== undefined && val !== null && String(val).trim() !== "")) {
        const field = tableSchema?.fields?.find(f => f.name === key);
        const dtype = field?.data_type?.toLowerCase();

        // Resolve operator defaulting (matches UI behavior)
        const operators = getOperatorsForType(dtype);
        const defaultOp = operators && operators.length > 0 ? operators[0].value : "contains";
        const op_resolved = data?.operator || defaultOp;

        filtersArray.push({
          column_key: key,
          column_value: isUnary ? "" : String(val).trim(), // Send empty string for unary if val missing
          filter_type: op_resolved,
        });
      }
    }
    onApply(filtersArray);
  };


  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      {/* Modal header with themed title and custom close button styling */}
      <Modal.Header closeButton style={{ background: theme.primary, color: "#fff" }}>
        <Modal.Title>Filter Records</Modal.Title>
        <style>{`.btn-close { filter: invert(1); }`}</style>
      </Modal.Header>

      {/* Scrollable modal body with one filter control per field */}
      <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <Form>
          {tableSchema?.fields?.map(field => {
            const dtype = (field.data_type || "").toLowerCase();
            const isBoolean = dtype === "boolean";
            const operators = getOperatorsForType(dtype);
            const fieldKey = field.name;
            const current = localFilters[fieldKey] || {};
            const currentOp = current.operator || operators[0].value;
            const currentVal = current.value || "";

            // Disable input if unary operator selected
            const isUnary = ["is_empty", "is_not_empty"].includes(currentOp);

            return (
              <Form.Group className="mb-3" controlId={`filter-${field.name}`} key={field.name}>
                <Form.Label style={{ color: theme.primary, fontWeight: 500 }}>
                  {field.display_name || field.name}
                </Form.Label>
                <div className="d-flex gap-2">
                  <Form.Select
                    style={{ maxWidth: '200px' }}
                    value={currentOp}
                    onChange={(e) => handleChange(fieldKey, 'operator', e.target.value)}
                  >
                    {operators.map(op => (
                      <option key={op.value} value={op.value}>
                        {op.symbol} {op.label}
                      </option>
                    ))}
                  </Form.Select>

                  {isBoolean ? (
                    <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: 1, border: '1px solid #dee2e6', borderRadius: '0.375rem', padding: '0 12px' }}>
                      <Form.Check
                        type="radio"
                        label="True"
                        name={`filter-bool-${fieldKey}`}
                        id={`filter-${fieldKey}-true`}
                        checked={String(currentVal).toLowerCase() === "true"}
                        onChange={() => handleChange(fieldKey, 'value', "true")}
                        className="mb-0"
                        disabled={isUnary}
                      />
                      <Form.Check
                        type="radio"
                        label="False"
                        name={`filter-bool-${fieldKey}`}
                        id={`filter-${fieldKey}-false`}
                        checked={String(currentVal).toLowerCase() === "false"}
                        onChange={() => handleChange(fieldKey, 'value', "false")}
                        className="mb-0"
                        disabled={isUnary}
                      />
                      {currentVal && (
                        <Button
                          mode="outlined"
                          small="true"
                          onClick={() => handleChange(fieldKey, 'value', "")}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  ) : (dtype === "datetime" || dtype === "timestamp" ? (
                    <div className="d-flex gap-2" style={{ flex: 1 }}>
                      <Form.Control
                        type="date"
                        value={currentVal ? currentVal.split("T")[0] : ""}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          const timePart = currentVal ? currentVal.split("T")[1] : "";
                          const newVal = newDate
                            ? (timePart ? `${newDate}T${timePart}` : newDate)
                            : "";
                          handleChange(fieldKey, 'value', newVal);
                        }}
                        disabled={isUnary}
                      />
                      <Form.Control
                        type="time"
                        step="1"
                        value={currentVal && currentVal.includes("T") ? currentVal.split("T")[1] : ""}
                        onChange={(e) => {
                          const newTime = e.target.value;
                          const datePart = currentVal ? currentVal.split("T")[0] : "";
                          if (datePart) {
                            const newVal = newTime ? `${datePart}T${newTime}` : datePart;
                            handleChange(fieldKey, 'value', newVal);
                          }
                        }}
                        disabled={isUnary}
                      />
                    </div>
                  ) : (
                    <Form.Control
                      type={
                        dtype === "integer" || dtype === "float" || dtype === "decimal" ? "number" :
                          dtype === "date" ? "date" :
                            "text"
                      }
                      step={dtype === "integer" ? "1" : "any"}
                      placeholder={`Filter by ${field.display_name || field.name}`}
                      value={currentVal}
                      onChange={(e) => handleChange(fieldKey, 'value', e.target.value)}
                      disabled={isUnary}
                    />
                  ))}
                </div>
              </Form.Group>
            );
          })}
        </Form>
      </Modal.Body>

      {/* Footer actions: close without applying or apply+close */}
      <Modal.Footer>
        <Button mode="outlined" onClick={onClose}>Close</Button>
        <Button
          mode="primary-bold"
          onClick={() => {
            handleApply();
            onClose();
          }}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
}


/**
 * TableViewPage
 * -------------
 * Top-level page component which:
 * - Resolves the effective table name (from props or route)
 * - Manages pagination, search, and filters
 * - Fetches data via React Query
 * - Integrates with DynamicTable and UI chrome (toolbar, paginator, dialogs)
 *
 * Props:
 * - tableName (optional): override table name instead of reading from route
 * - mode: "action" (default) or "select" to control available row actions
 * - onSelect: callback used in "select" mode when a row is chosen
 */
export default function TableViewPage({ tableName = null, mode = "action", onSelect }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { theme } = useTheme();
  const { setTableDisplayNames } = useBreadcrumbNames();
  const { showAlertDialog } = useAlertDialog();
  const queryClient = useQueryClient();
  const query = new URLSearchParams(location.search);
  const pageFromUrl = Number(query.get("page")) || 1;

  useEffect(() => {
    setPage(pageFromUrl);
  }, [pageFromUrl]);

  // Determine which table to load: explicit prop or URL param
  const effectiveTableName = tableName || params.tableName;

  // Multi-tenant / app-scoped identifiers from environment config
  const organizationId = envConfig.organizationId;
  const applicationId = envConfig.applicationId;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Search state: UI input vs actual query param sent to backend
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Filter state and modal visibility
  const [filters, setFilters] = useState([]);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [isStackedView, setIsStackedView] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [activeHistoryRecord, setActiveHistoryRecord] = useState(null); // Track specific record for history
  const [isExporting, setIsExporting] = useState(false);
  const [cachedSchema, setCachedSchema] = useState(null); // Valid schema for filter processing

  /**
   * React Query key:
   * Includes pagination, search, filters, and multi-tenant identifiers
   * so that each distinct combination is cached independently.
   */
  const tableQueryKey = useMemo(
    () => [
      "tableData",
      effectiveTableName,
      { page, pageSize, searchQuery, filters, organizationId, applicationId }
    ],
    [effectiveTableName, page, pageSize, searchQuery, filters, organizationId, applicationId]
  );

  const tableDetailsQueryKey = ['tableDetails', effectiveTableName, applicationId, organizationId];

  const {
    data: detailsData,
  } = useQuery({
    queryKey: tableDetailsQueryKey,
    queryFn: async ({ signal }) => {
      if (!effectiveTableName) return null;

      const payload = {
        application_id: applicationId,
        organization_id: organizationId,
        table_name: effectiveTableName,
      };

      const resp = await apiHelper.apiCall('/arcee/application/table/details/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal,
      });

      if (!resp.ok) {
        let j = {};
        try { j = await resp.json(); } catch (e) { }
        throw new Error(j?.error || `Failed to load table details (${resp.status})`);
      }

      return await resp.json();
    },
    enabled: !!effectiveTableName,
    staleTime: envConfig.staleTimeMs,
    retry: 1,
  });

  // --- Permission Handling ---
  // --- Permission Handling ---
  // Derive permissions directly from detailsData (populated by ApplicationTableSerializer)
  const canRead = detailsData?.user_permissions?.includes('read') ?? false;
  const canCreate = detailsData?.user_permissions?.includes('create') ?? false;
  const canUpdate = detailsData?.user_permissions?.includes('update') ?? false;
  const canDelete = detailsData?.user_permissions?.includes('delete') ?? false;
  const userDetails = {}; // Placeholder if needed, but permissions are now direct
  // ---------------------------

  /**
   * Fetch function used by React Query:
   * - POST to backend with table name, scope ids, filters
   * - Sends page, page_size, and search as query params
   * - Throws on non-OK status so React Query can surface errors
   */
  const fetchTableFn = async ({ signal }) => {
    if (!effectiveTableName) return null;

    // Transform filters for API (Timezone Conversion)
    // Use cachedSchema to ensure we have field definitions even if tableData is reloading
    const apiFilters = processFiltersForApi(filters, cachedSchema);

    const url = `/arcee/application/table/fetch/?page=${page}&page_size=${pageSize}&search=${encodeURIComponent(searchQuery || "")}`;
    const body = {
      table_name: effectiveTableName,
      organization_id: organizationId,
      application_id: applicationId,
      filters: apiFilters || []
    };

    const resp = await apiHelper.apiCall(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal
    });

    if (!resp.ok) {
      let errText = `Failed to fetch table data (${resp.status})`;
      try {
        const j = await resp.json();
        if (j && j.error) errText = j.error;
      } catch (e) { /* ignore JSON parse errors */ }
      throw new Error(errText);
    }

    return await resp.json();
  };

  /**
   * Main data fetch using React Query:
   * - keepPreviousData: smooth pagination/loading
   * - staleTime: short caching window
   * - onSuccess: if current page becomes empty, step back one page
   */
  const {
    data: tableData,
    error,
    isLoading,
    isFetching,
    refetch
  } = useQuery({
    queryKey: tableQueryKey,
    queryFn: fetchTableFn,
    enabled: !!effectiveTableName,
    keepPreviousData: true,
    keepPreviousData: true,
    staleTime: envConfig.staleTimeMs,
    retry: 1,
    onSuccess: (data) => {
      if (data && Array.isArray(data.records) && data.records.length === 0 && page > 1) {
        navigate(`?page=${page - 1}`, { replace: true });
      }
    }
  });

  /**
   * Force refetch when location changes (e.g. navigating back from another page).
   * dependent on location.key which changes on every history push/pop.
   */
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const newPage = Number(query.get("page")) || 1;

    // Update page BEFORE refetch
    setPage(newPage);

    // Now trigger refetch using correct page
    refetch();
  }, [location.search]);

  /**
   * Update breadcrumb display names and cached schema whenever a new table schema is loaded.
   * This allows other parts of the app to show a friendly table name and ensures filters can be processed.
   */
  useEffect(() => {
    if (tableData?.table_schema) {
      setCachedSchema(tableData.table_schema);
      setTableDisplayNames(prev => ({
        ...prev,
        [tableData.table_schema.table_name]:
          tableData.table_schema.display_name || tableData.table_schema.table_name,
      }));
    }
  }, [tableData, setTableDisplayNames]);

  /**
   * React Query mutation for deleting a record:
   * - Sends DELETE to backend with table name and ids
   * - Invalidates cached table queries for this table on success
   */
  const deleteMutation = useMutation({
    mutationFn: async (recordId) => {
      const payload = {
        table_name: effectiveTableName,
        organization_id: organizationId,
        application_id: applicationId,
        id: recordId
      };

      const resp = await apiHelper.apiCall("/arcee/application/table/delete-record/", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!resp.ok) {
        let errObj = { error: "Delete failed" };
        try { errObj = await resp.json(); } catch (e) { /* ignore */ }
        throw new Error(errObj.error || `Delete failed (${resp.status})`);
      }
      return resp.json();
    },
    onSuccess: () => {
      // Invalidate all queries for this table so list refreshes
      queryClient.invalidateQueries({
        queryKey: ["tableData", effectiveTableName],
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === "tableData" &&
          q.queryKey[1] === effectiveTableName
      });
    },
  });

  /**
   * Commit current searchInput as the active searchQuery and reset to page 1.
   * This decouples typing from actual backend queries.
   */
  const onSearchClick = () => {
    setPage(1);
    setSearchQuery(searchInput.trim());
  };

  // Clear both search input and active query and go back to first page
  const handleClearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setPage(1);
  };

  // Derived flag used by toolbar to highlight active search state
  const isSearchActive = !!searchQuery && searchInput === searchQuery;

  /**
   * Apply filters coming from FiltersModal:
   * - Strip out created_by_id filter for safety
   * - Reset pagination to first page
   */
  const onApplyFilters = (newFilters) => {
    const safeFilters = newFilters.filter(f => f.column_key !== "created_by_id");
    setPage(1);
    setFilters(safeFilters);
  };

  /**
   * Handle delete from DynamicTable:
   * - Show confirmation dialog
   * - If confirmed, run delete mutation and show success/error dialogs
   */
  const onDeleteRecord = (record) => {
    showAlertDialog({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this record?",
      type: "info",
      okLabel: "Yes",
      cancelLabel: "Cancel",
      showCancel: true,
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(record.id);
          showAlertDialog({
            title: "Success",
            message: "Record deleted successfully!",
            type: "success",
            okLabel: "Close",
            showCancel: false
          });
        } catch (err) {
          showAlertDialog({
            title: "Error",
            message: err?.message || "Delete failed",
            type: "error",
            okLabel: "Close",
            showCancel: false
          });
        }
      },
    });
  };

  // Pagination helpers for display text and Paginator component
  const totalRecords = tableData?.count || 0;
  const startRecord = (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, totalRecords);

  // Navigate to Create form for this table
  const onCreate = () => {
    navigate(
      `/dashboard/tables/${effectiveTableName}/create`,
      { state: { fromPage: page } }
    );
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      // Use the same parameters as the fetch API plus the format
      const url = `/arcee/application/table/export/?page=${page}&page_size=${pageSize}&search=${encodeURIComponent(searchQuery || "")}`;
      const body = {
        table_name: effectiveTableName,
        organization_id: organizationId,
        application_id: applicationId,
        filters: filters,
        format: format
      };

      const resp = await apiHelper.apiCall(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (!resp.ok) {
        let errText = `Failed to export data (${resp.status})`;
        try {
          const j = await resp.json();
          if (j && j.error) errText = j.error;
        } catch (e) { /* ignore JSON parse errors */ }
        throw new Error(errText);
      }

      showAlertDialog({
        title: "Info",
        message: "You can download the file from the Exports section once it ready.",
        type: "info",
        okLabel: "Close",
        showCancel: false
      });

    } catch (err) {
      showAlertDialog({
        title: "Error",
        message: err?.message || "Export failed",
        type: "error",
        okLabel: "Close",
        showCancel: false
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Early guard while table name is not yet resolved
  if (!effectiveTableName) {
    return <div>Loading...</div>;
  }



  /**
   * Filter out internal fields (e.g., created_by_id) from both schema
   * and records before passing them to DynamicTable.
   */
  let filteredSchema = null;
  let filteredRecords = [];
  if (tableData) {
    filteredSchema = {
      ...tableData.table_schema,
      fields: tableData.table_schema.fields.filter(f => f.name !== "created_by_id")
    };
    filteredRecords = (tableData.records || []).map(r => {
      const copy = { ...r };

      // Replace values with labels for string/text fields with option lists
      // Use detailsData if available as it contains the authoritative ui_rules
      const schemaToUse = detailsData && detailsData.fields ? detailsData : filteredSchema;

      schemaToUse.fields.forEach(field => {
        const type = (field.data_type || "").toLowerCase();
        if ((type === "string" || type === "text") && copy[field.name] !== undefined && copy[field.name] !== null) {
          const options = getFieldOptionsForRole(field, null);
          if (options && options.length > 0) {
            const valStr = String(copy[field.name]);
            const match = options.find(o => o.value === valStr);
            if (match) {
              copy[field.name] = match.label;
            }
          }
        }
      });

      delete copy.created_by_id;
      return copy;
    });
  }

  return (
    <div style={{ overflowX: "hidden" }}>
      <div className="container mt-4" style={{ maxWidth: 3000, margin: "0 auto" }}>
        {/* Global loading overlays (fetching list and deleting a record) */}
        {/* Global loading overlays (fetching list, deleting a record, or exporting) */}
        <ProgressBarDialog
          open={isLoading || isFetching || deleteMutation.isLoading || isExporting}
          text_message={
            isExporting ? "Please wait, we are preparing  your file..." :
              deleteMutation.isLoading ? "Deleting record..." :
                "Loading table data..."
          }
        />

        {/* Error dialog in normal mode (navigates back to tables on close) */}
        {mode !== 'select' && (
          <AlertDialog
            open={!!error}
            title="Error"
            message={error?.message || error}
            type="error"
            okLabel="Close"
            showCancel={false}
            onOk={() => { refetch(); navigate(`/dashboard/tables/`); }}
          />
        )}

        {/* Inline error box in select mode (no navigation side effects) */}
        {mode === 'select' && error && (
          <div
            style={{
              color: 'red',
              padding: '10px',
              border: '1px solid #ffcccb',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}
          >
            {error?.message || error}
          </div>
        )}

        {/* Main header + toolbar + active filters summary */}
        {!isLoading && !error && tableData && (
          <div style={{ marginBottom: 20 }}>
            {/* Title row with table name and data scope badge */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 8
              }}
            >
              <h3 style={{ margin: 0, fontWeight: 700, color: '#1a1f36' }}>
                {tableData.table_schema?.display_name || effectiveTableName}
              </h3>

              {tableData.data_scope && (
                <span
                  style={{
                    padding: "4px 10px",
                    borderRadius: 20,
                    fontSize: 12,
                    backgroundColor: tableData.data_scope === "all" ? "#e0f7e9" : "#e8f0ff",
                    color: tableData.data_scope === "all" ? "#0f6b2f" : "#174ea6",
                    fontWeight: 600,
                  }}
                >
                  {tableData.data_scope === "all" ? "All records" : "Own records"}
                </span>
              )}
            </div>

            {/* Unified toolbar: search, filters, export, create */}
            <TableActionsToolbar
              searchInput={searchInput}
              onSearchChange={(e) => setSearchInput(e.target.value)}
              onSearchSubmit={onSearchClick}
              onClearSearch={handleClearSearch}
              isSearchActive={isSearchActive}
              onOpenFilters={() => setFilterModalOpen(true)}
              onResetFilters={() => { setFilters([]); setPage(1); }}
              hasFilters={filters.length > 0}
              onExport={handleExport}
              onCreate={onCreate}
              onHistory={() => {
                setActiveHistoryRecord(null);
                setIsHistoryOpen(true);
              }}
              showCreate={mode !== "select" && canCreate}
              showHistory={canRead}
              isStackedView={isStackedView}
              onToggleStackedView={() => setIsStackedView(!isStackedView)}
            />

            {/* Badges for currently active filters */}
            {filters.length > 0 && (
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center"
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    color: "#6b7280",
                    fontWeight: 500
                  }}
                >
                  Active Filters:
                </span>
                {filters.map((filter, i) => {
                  const field =
                    tableData?.table_schema?.fields?.find(f => f.name === filter.column_key);
                  const label = field ? field.display_name || field.name : filter.column_key;
                  return (
                    <span
                      key={i}
                      style={{
                        background: "#eff6ff",
                        color: theme.primary,
                        padding: "2px 8px",
                        borderRadius: 6,
                        fontSize: 12,
                        border: `1px solid ${theme.primary}40`
                      }}
                    >
                      {label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Dynamic table and paginator */}
        {!isLoading && !error && tableData && (
          <>
            <DynamicTable
              tableSchema={filteredSchema}
              records={filteredRecords}
              page={page}
              pageSize={pageSize}
              forceStacked={isStackedView}
              onView={(record) =>
                navigate(
                  `/dashboard/tables/${effectiveTableName}/${record.id}`,
                  { state: { tableSchema: filteredSchema } }
                )
              }
              onEdit={canUpdate ? (record) => {
                const originalRecord = tableData.records.find(r => r.id === record.id) || record;
                navigate(
                  `/dashboard/tables/${effectiveTableName}/${record.id}/update`,
                  { state: { record: originalRecord, mode: 'update', tableSchema: filteredSchema, fromPage: page } }
                );
              } : null}
              onDelete={canDelete ? onDeleteRecord : null}
              mode={mode}
              onSelect={onSelect}
              onHistory={canRead ? (record) => {
                setActiveHistoryRecord(record);
                setIsHistoryOpen(true);
              } : null}
            />

            {/* Pagination footer with record range summary */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 12
              }}
            >
              {Math.ceil(totalRecords / pageSize) > 1 && totalRecords > 0 && (
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: 14,
                    minWidth: 200
                  }}
                >
                  Showing {startRecord}-{endRecord} of {totalRecords} records
                </div>
              )}

              <Paginator
                page={page}
                pageCount={Math.max(1, Math.ceil(totalRecords / pageSize))}
                onPrevious={(p) => navigate(`?page=${p}`)}
                onNext={(p) => navigate(`?page=${p}`)}
                onPage={(p) => navigate(`?page=${p}`)}
                maxPagesToShow={5}
              />
            </div>
          </>
        )}

        {/* Filters configuration modal (uses filteredSchema to hide internal fields) */}
        <FiltersModal
          show={isFilterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          onApply={onApplyFilters}
          tableSchema={filteredSchema}
          initialFilters={filters}
        />

        <AuditHistoryModal
          show={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
          filters={{
            organization_id: organizationId,
            application_id: applicationId,
            table_name: effectiveTableName,
            record_id: activeHistoryRecord?.id
          }}
        />
      </div>
    </div>
  );
}
